import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { LuSparkles, LuSend, LuFlaskConical } from 'react-icons/lu'
import { MdSearch } from 'react-icons/md'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CHROME_HEADER_GRADIENT, CHROME_GLASS_BORDER } from '../../../theme/chrome'
import { LAB_APPS, isLabAppEnabled } from '../../../config/labApps'
import { useUserModules } from '../../../contexts/UserModuleContext'
import { ModuleType } from '../../../types/modules'

// Le catalogue vit dans src/config/labApps.ts, partagé avec le panneau
// d'administration : une application ajoutée là-bas apparaît ici et devient
// activable par utilisateur sans autre geste.

export default function LabLanding() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useUserModules()
  const toast = useToast()
  const [searchQuery, setSearchQuery] = useState('')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [moduleName, setModuleName] = useState('')
  const [context, setContext] = useState('')
  const [sending, setSending] = useState(false)

  // Where module requests are sent. Change here (or wire a backend endpoint later).
  const MODULE_REQUEST_TO = 'acherar@careflai.com'

  const handleSendRequest = async () => {
    if (!moduleName.trim()) return
    setSending(true)
    // Open the user's mail client with the request pre-filled to the Careflai
    // team. (Interim until a dedicated backend email endpoint is wired.)
    const subject = `Demande de module — ${moduleName.trim()}`
    const body =
      `Module souhaité : ${moduleName.trim()}\n\n` +
      `Contexte et besoins :\n${context.trim() || '—'}`
    window.location.href =
      `mailto:${MODULE_REQUEST_TO}` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`
    await new Promise(r => setTimeout(r, 400))
    setSending(false)
    setModuleName('')
    setContext('')
    onClose()
    toast({
      title: t('lab.addNewSpecialtyModal.success'),
      status: 'success',
      duration: 4000,
      isClosable: true,
      position: 'top',
    })
  }

  // Les applications fermées à cet utilisateur ne s'affichent pas : un
  // interrupteur dans l'administration doit se voir ici, pas seulement
  // empêcher l'accès une fois la carte cliquée.
  const labPermissions = user?.modules?.find(
    (m) => m.moduleType === ModuleType.LAB && m.isEnabled,
  )?.permissions
  const openSpecialties = LAB_APPS.filter((app) => isLabAppEnabled(labPermissions, app.id))

  const filteredSpecialties = openSpecialties.filter((specialty) => {
    const name = t(`lab.specialties.${specialty.id}.name`)
    const subtitle = t(`lab.specialties.${specialty.id}.subtitle`)
    const description = t(`lab.specialties.${specialty.id}.description`)
    return (
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })

  const handleSpecialtyClick = (specialtyId: string) => {
    navigate(`/lab/${specialtyId}`)
  }

  return (
    <Box position="relative">
      {/* Decorative Background Elements */}
      <Box
        position="absolute"
        top="-100px"
        right="-100px"
        width="400px"
        height="400px"
        borderRadius="full"
        bgGradient="linear(135deg, #a78bfa, #7c3aed)"
        opacity={0.28}
        filter="blur(80px)"
        pointerEvents="none"
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom="100px"
        left="-150px"
        width="500px"
        height="500px"
        borderRadius="full"
        bgGradient="linear(135deg, #22d3ee, #06b6d4)"
        opacity={0.18}
        filter="blur(100px)"
        pointerEvents="none"
        zIndex={0}
      />

      {/* Header Section with Gradient */}
      <VStack spacing={6} align="stretch" mb={8} position="relative" zIndex={1}>
        <Box
          bgGradient={CHROME_HEADER_GRADIENT}
          borderRadius="2xl"
          px={6}
          py={5}
          position="relative"
          overflow="hidden"
          boxShadow="xl"
          sx={{ '&::after': CHROME_GLASS_BORDER }}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        >
          <VStack spacing={3} align="start" position="relative" zIndex={1}>
            <HStack spacing={3}>
              <Box
                p={2.5}
                bg="whiteAlpha.300"
                backdropFilter="blur(10px)"
                borderRadius="lg"
                border="1px solid"
                borderColor="whiteAlpha.400"
              >
                <Icon as={LuSparkles} boxSize={6} color="white" />
              </Box>
              <VStack spacing={0.5} align="start">
                <Heading as="h1" size="lg" color="white" fontWeight="bold" letterSpacing="tight">
                  {t('lab.title')}
                </Heading>
                <Text fontSize="sm" color="whiteAlpha.900" fontWeight="medium">
                  {t('lab.subtitle')}
                </Text>
              </VStack>
            </HStack>

            {/* Search Bar */}
            <InputGroup maxW="600px" size="md" mt={2}>
              <InputLeftElement pointerEvents="none" h="full">
                <MdSearch style={{ color: 'purple', fontSize: '20px' }} />
              </InputLeftElement>
              <Input
                placeholder={t('lab.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="white"
                borderRadius="lg"
                border="none"
                fontSize="md"
                h="48px"
                pl={12}
                boxShadow="lg"
                _focus={{
                  boxShadow: '0 0 0 4px rgba(167, 139, 250, 0.3)',
                  transform: 'translateY(-2px)',
                }}
                _placeholder={{
                  color: 'gray.400',
                }}
                transition="all 0.2s"
              />
            </InputGroup>
          </VStack>
        </Box>
      </VStack>

      {/* AI Applications Grid */}
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6} position="relative" zIndex={1}>
        {filteredSpecialties.map((specialty) => {
          const accentBase = specialty.color.split('.')[0]
          // Module-colour glow for the hover state (identity kept per module).
          const glow = ({
            teal: 'rgba(20,184,166,0.38)',
            purple: 'rgba(168,85,247,0.38)',
            cyan: 'rgba(34,211,238,0.38)',
            blue: 'rgba(59,130,246,0.38)',
          } as Record<string, string>)[accentBase] || 'rgba(124,58,237,0.32)'
          return (
            <Card
              key={specialty.id}
              cursor="pointer"
              onClick={() => handleSpecialtyClick(specialty.id)}
              role="group"
              transition="all 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
              _hover={{
                transform: 'translateY(-6px)',
                boxShadow: `0 26px 52px -18px ${glow}, 0 0 0 1px ${glow}`,
              }}
              bg="white"
              borderWidth="0"
              borderRadius="2xl"
              overflow="hidden"
              position="relative"
              boxShadow="md"
            >
              {/* Colored top accent */}
              <Box
                h="4px"
                bgGradient={`linear(90deg, ${specialty.color}, ${accentBase}.300)`}
              />
              {/* Soft module-colour wash, top-right — appears on hover for depth */}
              <Box
                position="absolute"
                top="-40px"
                right="-40px"
                w="180px"
                h="180px"
                borderRadius="full"
                bg={`radial-gradient(circle, ${glow} 0%, transparent 70%)`}
                opacity={0}
                transition="opacity 0.35s"
                pointerEvents="none"
                _groupHover={{ opacity: 1 }}
              />

              {/* Badge — positionné en absolu pour ne jamais superposer le titre */}
              {specialty.badge && (
                <Badge
                  position="absolute"
                  top="18px"
                  right="18px"
                  colorScheme={specialty.badgeColor}
                  variant="solid"
                  borderRadius="full"
                  px={2.5}
                  py={1}
                  fontSize="2xs"
                  fontWeight="bold"
                  textTransform="uppercase"
                  boxShadow="sm"
                  zIndex={2}
                >
                  {t(`lab.badges.${specialty.badge}`)}
                </Badge>
              )}

              <CardBody p={7} position="relative" zIndex={1}>
                <VStack spacing={5} align="stretch">
                  {/* Icon + title row */}
                  <HStack spacing={4} align="center" pr={specialty.badge ? 16 : 0}>
                    <Box
                      w="52px"
                      h="52px"
                      borderRadius="xl"
                      bgGradient={`linear(135deg, ${specialty.color}, ${accentBase}.300)`}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow={`0 8px 22px -6px ${glow}`}
                      flexShrink={0}
                      transition="all 0.3s"
                      _groupHover={{ transform: 'rotate(6deg) scale(1.08)', boxShadow: `0 12px 28px -6px ${glow}` }}
                    >
                      <specialty.icon style={{ fontSize: '26px', color: 'white' }} />
                    </Box>
                    <VStack spacing={0.5} align="start" minW={0}>
                      <Heading
                        as="h3"
                        size="md"
                        fontWeight="bold"
                        color="gray.800"
                        lineHeight="shorter"
                        noOfLines={1}
                      >
                        {t(`lab.specialties.${specialty.id}.name`)}
                      </Heading>
                      {t(`lab.specialties.${specialty.id}.subtitle`) && (
                        <Text fontSize="xs" color="gray.400" fontWeight="medium" noOfLines={1}>
                          {t(`lab.specialties.${specialty.id}.subtitle`)}
                        </Text>
                      )}
                    </VStack>
                  </HStack>

                  {/* Description */}
                  <Text color="gray.500" fontSize="sm" lineHeight="1.7" noOfLines={3}>
                    {t(`lab.specialties.${specialty.id}.description`)}
                  </Text>

                  {/* CTA — pill that fills with the module colour on hover */}
                  <HStack
                    alignSelf="flex-start"
                    spacing={1.5}
                    color={specialty.color}
                    fontWeight="semibold"
                    fontSize="sm"
                    px={4}
                    py={2}
                    borderRadius="full"
                    borderWidth="1px"
                    borderColor={`${accentBase}.200`}
                    bg={`${accentBase}.50`}
                    transition="all 0.25s"
                    _groupHover={{
                      gap: 2,
                      bgGradient: `linear(135deg, ${specialty.color}, ${accentBase}.400)`,
                      color: 'white',
                      borderColor: 'transparent',
                      boxShadow: `0 8px 20px -6px ${glow}`,
                    }}
                  >
                    <Text>{t(`lab.specialties.${specialty.id}.button`)}</Text>
                    <Text
                      transition="transform 0.2s"
                      _groupHover={{ transform: 'translateX(4px)' }}
                    >
                      →
                    </Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          )
        })}

        {/* La tuile d'ajout ferme la grille : un carré pointillé, le grand
            « + », deux lignes — le geste de demande au rang des modules,
            plus une bannière qui coupe la page. */}
        {filteredSpecialties.length > 0 && (
          <Card
            cursor="pointer"
            onClick={onOpen}
            role="group"
            borderStyle="dashed"
            borderWidth="2px"
            borderColor="purple.200"
            bg="transparent"
            borderRadius="2xl"
            boxShadow="none"
            transition="all 0.3s ease"
            _hover={{
              borderColor: 'purple.400',
              bg: 'purple.50',
              transform: 'translateY(-6px)',
              boxShadow: '0 20px 40px -18px rgba(168,85,247,0.35)',
            }}
          >
            <CardBody p={7} display="flex" alignItems="center" justifyContent="center" minH="220px">
              <VStack spacing={4} textAlign="center">
                <Box
                  w="64px"
                  h="64px"
                  borderRadius="2xl"
                  bgGradient="linear(135deg, purple.500, pink.500)"
                  display="grid"
                  sx={{ placeItems: 'center' }}
                  boxShadow="0 10px 24px -8px rgba(168,85,247,0.5)"
                  transition="transform 0.3s ease"
                  _groupHover={{ transform: 'scale(1.08) rotate(90deg)' }}
                >
                  <Text fontSize="3xl" fontWeight="300" color="white" lineHeight={1} mt="-2px">+</Text>
                </Box>
                <VStack spacing={1}>
                  <Text fontWeight="bold" color="purple.700" fontSize="md">
                    {t('lab.addNewSpecialty')}
                  </Text>
                  <Text fontSize="xs" color="purple.400" maxW="200px">
                    {t('lab.addNewSpecialtySubtitle')}
                  </Text>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        )}
      </SimpleGrid>

      {/* No Results */}
      {filteredSpecialties.length === 0 && (
        <VStack spacing={4} py={16} textAlign="center">
          <Box
            p={6}
            bg="gray.100"
            borderRadius="full"
            display="inline-flex"
          >
            <MdSearch style={{ fontSize: '48px', color: '#A0AEC0' }} />
          </Box>
          <VStack spacing={2}>
            <Text fontSize="xl" fontWeight="bold" color="gray.700">
              {t('lab.noSpecialtiesFound')}
            </Text>
            <Text fontSize="md" color="gray.500">
              Aucun module trouvé pour "{searchQuery}"
            </Text>
          </VStack>
        </VStack>
      )}

      {/* Request module modal — squared, clinical/scientific styling. */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
        <ModalContent
          borderRadius="md"
          overflow="hidden"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="0 24px 64px -12px rgba(15, 23, 42, 0.45)"
        >
          <ModalHeader
            bgGradient="linear(135deg, #0f172a 0%, #1e293b 100%)"
            color="white"
            py={4}
            position="relative"
          >
            <Box
              position="absolute"
              inset={0}
              background="radial-gradient(circle at 88% 15%, rgba(45,212,191,0.18) 0%, transparent 55%)"
              pointerEvents="none"
            />
            <HStack spacing={3} position="relative" zIndex={1}>
              <Box
                w="36px"
                h="36px"
                borderRadius="md"
                bg="whiteAlpha.200"
                border="1px solid"
                borderColor="whiteAlpha.300"
                display="grid"
                sx={{ placeItems: 'center' }}
                color="teal.200"
              >
                <Icon as={LuFlaskConical} boxSize={5} />
              </Box>
              <Text fontSize="md" fontWeight="bold" letterSpacing="tight">
                {t('lab.addNewSpecialtyModal.title')}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" top={3.5} zIndex={2} />

          <ModalBody py={5}>
            <VStack spacing={5} align="stretch">
              <Text fontSize="sm" color="gray.500">
                {t('lab.addNewSpecialtyModal.subtitle')}
              </Text>

              <FormControl isRequired>
                <FormLabel
                  fontSize="2xs"
                  fontWeight="700"
                  letterSpacing="wider"
                  textTransform="uppercase"
                  color="gray.500"
                  mb={1.5}
                >
                  {t('lab.addNewSpecialtyModal.moduleName')}
                </FormLabel>
                <Input
                  value={moduleName}
                  onChange={e => setModuleName(e.target.value)}
                  placeholder={t('lab.addNewSpecialtyModal.moduleNamePlaceholder')}
                  borderRadius="md"
                  focusBorderColor="teal.500"
                  size="md"
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  fontSize="2xs"
                  fontWeight="700"
                  letterSpacing="wider"
                  textTransform="uppercase"
                  color="gray.500"
                  mb={1.5}
                >
                  {t('lab.addNewSpecialtyModal.context')}
                </FormLabel>
                <Textarea
                  value={context}
                  onChange={e => setContext(e.target.value)}
                  placeholder={t('lab.addNewSpecialtyModal.contextPlaceholder')}
                  rows={4}
                  resize="none"
                  borderRadius="md"
                  focusBorderColor="teal.500"
                  fontSize="sm"
                />
                <FormHelperText fontSize="2xs" color="gray.400">
                  {t('lab.addNewSpecialtyModal.contextHelper')}
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2}>
            <Button variant="ghost" onClick={onClose} size="sm" borderRadius="md">
              {t('lab.addNewSpecialtyModal.cancel')}
            </Button>
            <Button
              bgGradient="linear(135deg, teal.500, cyan.600)"
              color="white"
              _hover={{ bgGradient: 'linear(135deg, teal.600, cyan.700)' }}
              leftIcon={<Icon as={LuSend} />}
              onClick={handleSendRequest}
              isLoading={sending}
              loadingText={t('lab.addNewSpecialtyModal.sending')}
              isDisabled={!moduleName.trim()}
              size="sm"
              borderRadius="md"
            >
              {t('lab.addNewSpecialtyModal.send')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
